// Multilingual number to words conversion utility
// @ts-ignore
import 'number-to-arabic-words';
// @ts-ignore
import { ToWords } from 'to-words';

export type Language = 'en' | 'fr' | 'ar';

// Arabic number words (using Arabic numerals with Arabic text)
// Kept for fallback or specific Arabic usage if needed by number-to-arabic-words (though likely not needed if library handles it)
const arabicWords = {
  ones: ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة'],
  tens: ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'],
  teens: ['عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر', 'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر'],
  hundreds: 'مائة',
  thousands: 'ألف',
  millions: 'مليون',
  and: 'و'
};

// Currency words
const currencyWords = {
  en: {
    MAD: { singular: 'Dirham', plural: 'Dirhams' },
    USD: { singular: 'Dollar', plural: 'Dollars' },
    EUR: { singular: 'Euro', plural: 'Euros' },
    GBP: { singular: 'Pound', plural: 'Pounds' },
    CAD: { singular: 'Canadian Dollar', plural: 'Canadian Dollars' },
    AUD: { singular: 'Australian Dollar', plural: 'Australian Dollars' },
    CHF: { singular: 'Swiss Franc', plural: 'Swiss Francs' },
    JPY: { singular: 'Yen', plural: 'Yen' },
    CNY: { singular: 'Yuan', plural: 'Yuan' },
    cent: { singular: 'Cent', plural: 'Cents' }
  },
  fr: {
    MAD: { singular: 'Dirham', plural: 'Dirhams' },
    USD: { singular: 'Dollar', plural: 'Dollars' },
    EUR: { singular: 'Euro', plural: 'Euros' },
    GBP: { singular: 'Livre Sterling', plural: 'Livres Sterling' },
    CAD: { singular: 'Dollar Canadien', plural: 'Dollars Canadiens' },
    AUD: { singular: 'Dollar Australien', plural: 'Dollars Australiens' },
    CHF: { singular: 'Franc Suisse', plural: 'Francs Suisses' },
    JPY: { singular: 'Yen', plural: 'Yen' },
    CNY: { singular: 'Yuan', plural: 'Yuans' },
    cent: { singular: 'Centime', plural: 'Centimes' }
  },
  ar: {
    MAD: { singular: 'درهم', plural: 'دراهم' },
    USD: { singular: 'دولار', plural: 'دولارات' },
    EUR: { singular: 'يورو', plural: 'يورو' },
    GBP: { singular: 'جنيه', plural: 'جنيهات' },
    CAD: { singular: 'دولار كندي', plural: 'دولارات كندية' },
    AUD: { singular: 'دولار أسترالي', plural: 'دولارات أسترالية' },
    CHF: { singular: 'فرنك سويسري', plural: 'فرنكات سويسرية' },
    JPY: { singular: 'ين', plural: 'ين' },
    CNY: { singular: 'يوان', plural: 'يوان' },
    cent: { singular: 'سنتا', plural: 'سنتات' }
  }
};

export function numberToWords(num: number, language: Language = 'en'): string {
  if (language === 'ar') {
    // Existing Arabic logic fallback (simple implementation or library usage)
    if (num === 0) return 'صفر';
    // @ts-ignore
    if (typeof (window as any).toArabicWord === 'function') {
      // @ts-ignore
      return (window as any).toArabicWord(num);
    }
    // Fallback to manual if window.toArabicWord is not available (though it should be)
    return num.toString();
  }

  const localeCode = language === 'fr' ? 'fr-FR' : 'en-US';
  const toWords = new ToWords({
    localeCode: localeCode,
    converterOptions: {
      currency: false,
      ignoreDecimal: true,
      ignoreZeroCurrency: true,
    }
  });

  return toWords.convert(num);
}

export function calculateTotalInWords(
  total: number,
  currency: string,
  language: Language = 'en'
): string {
  // Handle Arabic using the existing logic
  if (language === 'ar') {
    const integerPart = Math.floor(total);
    const decimalPart = Math.round((total - integerPart) * 100);
    const currencyInfo = currencyWords['ar'][currency as keyof typeof currencyWords['ar']] || currencyWords['ar'].MAD;
    const centInfo = currencyWords['ar'].cent;

    // @ts-ignore
    const integerWords = (window as any).toArabicWord ? (window as any).toArabicWord(integerPart) : numberToWords(integerPart, 'ar');
    let result = integerWords + ' ' + currencyInfo.singular;

    if (decimalPart > 0) {
      result += ' و ';
      // @ts-ignore
      const decimalWords = (window as any).toArabicWord ? (window as any).toArabicWord(decimalPart) : numberToWords(decimalPart, 'ar');
      result += decimalWords + ' ' + centInfo.singular;
    }
    return result.toUpperCase();
  }

  // Handle English and French using to-words
  const localeCode = language === 'fr' ? 'fr-FR' : 'en-US';
  const currencyInfo = (currencyWords[language] as any)[currency] || (currencyWords[language] as any).MAD;
  const centInfo = (currencyWords[language] as any).cent;

  const toWords = new ToWords({
    localeCode: localeCode,
    converterOptions: {
      currency: true,
      ignoreDecimal: false,
      ignoreZeroCurrency: false,
      doNotAddOnly: false,
      currencyOptions: {
        name: currencyInfo.singular,
        plural: currencyInfo.plural,
        symbol: currency,
        fractionalUnit: {
          name: centInfo.singular,
          plural: centInfo.plural,
          symbol: '',
        },
      }
    }
  });

  try {
    const result = toWords.convert(total);
    return result.toUpperCase();
  } catch (e) {
    console.error("Error converting number to words", e);
    return total.toString();
  }
}
